import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, fullName, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Eye, ListChecks, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import PdfLangButton from '../components/PdfLangButton';
import { PatientCombobox } from '../components/PatientCombobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function NewInstructionsDialog({ open, onOpenChange, onCreated, initialPatient }) {
  const [patient_id, setPatientId] = useState(initialPatient || '');
  const [surgery_date, setSurgeryDate] = useState('');
  const [custom_notes, setCustomNotes] = useState('');
  useEffect(() => {
    if (open) {
      setPatientId(initialPatient || '');
      setSurgeryDate('');
      setCustomNotes('');
    }
  }, [open, initialPatient]);
  const submit = async () => {
    if (!patient_id) return toast.error('Selecciona paciente');
    try {
      const res = await api.post('/api/instructions', { patient_id, surgery_date: surgery_date || null, custom_notes });
      toast.success('Indicaciones generadas');
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Generar indicaciones para el día de la cirugía</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-3 rounded">
            Se generará un documento con las <strong>indicaciones estándar</strong> de la clínica para el día de la cirugía: ayuno 6h (4h agua), cara lavada, sin maquillaje, medicación habitual con un trago de agua, suspender vitaminas, suplementos, Adiro y minoxidil 5 días antes y Mounjaro, Ozempic o similares 7 días antes, acompañante imprescindible, cirugía ambulatoria con sedación, estancia 2-3 horas.
          </div>
          <div><Label>Paciente</Label>
            <PatientCombobox value={patient_id} onChange={setPatientId} dataTestid="instr-patient-select" />
          </div>
          <div><Label>Fecha de la cirugía (opcional)</Label><Input type="date" value={surgery_date} onChange={(e) => setSurgeryDate(e.target.value)} /></div>
          <div><Label>Notas específicas (opcional)</Label><Textarea value={custom_notes} onChange={(e) => setCustomNotes(e.target.value)} placeholder="Indicaciones adicionales para este paciente…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="instr-create-button">Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InstructionsPage() {
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(!!initialPatient);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  const fetchItems = async () => { setLoading(true); try { const r = await api.get('/api/instructions'); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { fetchItems(); }, []);
  const onDelete = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/api/instructions/${id}`); toast.success('Eliminado'); fetchItems(); };

  return (
    <div>
      <PageHeader title="Indicaciones día de la cirugía" subtitle={`${items.length} documentos generados`}
        actions={<Button onClick={() => setOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="instr-new-button"><Plus className="w-4 h-4 mr-2" /> Nuevas indicaciones</Button>} />
      <div className="cjo-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : items.length === 0 ? <EmptyState icon={ListChecks} title="Sin documentos" description="Las indicaciones se generan automáticamente al crear un presupuesto." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th><th className="py-3 px-4 font-semibold text-[#13231E]">Fecha cirugía</th><th className="py-3 px-4 font-semibold text-[#13231E]">Notas</th><th className="py-3 px-4 font-semibold text-[#13231E]">Generado</th><th></th></tr></thead>
            <tbody>{items.map(it => <tr key={it.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="instr-row">
              <td className="py-3 px-4 font-medium">{it.patient_name}</td>
              <td className="py-3 px-4 text-xs">{it.surgery_date || '—'}</td>
              <td className="py-3 px-4 text-xs truncate max-w-[280px]">{it.custom_notes || '—'}</td>
              <td className="py-3 px-4 text-xs">{formatDate(it.created_at)}{it.auto_generated_from_budget && <span className="ml-1 text-[10px] uppercase tracking-wider bg-[rgba(46,110,94,0.18)] text-[#13231E] px-1.5 py-0.5 rounded">auto</span>}</td>
              <td className="py-3 px-4 text-right"><div className="flex gap-1 items-center justify-end">
                <PdfLangButton
                  baseUrl={`${BACKEND_URL}/api/instructions/${it.id}/pdf`}
                  filename={`indicaciones_${it.id}.pdf`}
                  title={`Indicaciones · ${it.patient_name}`}
                  onOpen={(p) => setPreview({ open: true, ...p })}
                  dataTestid="instr-pdf-button"
                >
                  <Button size="sm" variant="outline" title="Ver indicaciones (elige idioma)"><Eye className="w-4 h-4" /></Button>
                </PdfLangButton>
                <Button size="sm" variant="ghost" onClick={() => onDelete(it.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div></td>
            </tr>)}</tbody></table></div>}
      </div>
      <NewInstructionsDialog open={open} onOpenChange={setOpen} initialPatient={initialPatient} onCreated={fetchItems} />
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
    </div>
  );
}
